import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { tenderService, boostTender, pinTender, unpinTender, liveMarketService } from "../../services/api";
import { MoreHorizontal, Search } from "lucide-react";
import ConfirmationModal from "./ConfirmationModal";
import PostedByAgentLine from "./PostedByAgentLine";
import RequestPreviewModal from "../common/RequestPreviewModal";
import AttachedDocLink from "../common/AttachedDocLink";
import CountryFlag from "../transfer-windows/CountryFlag";
import RequestDraftBadge from "./RequestDraftBadge";
import RequestDraftActions from "./RequestDraftActions";
import RequestArchivedBadge from "./RequestArchivedBadge";
import RequestArchiveActions from "./RequestArchiveActions";
import RequestConfirmDialog from "./RequestConfirmDialog";
import toast from "react-hot-toast";
import { usePermission } from "../../hooks/usePermission";
import { useClubTenderActions } from "../../hooks/clubtender/useClubTenderActions";
import {
  useTransferPointsCost,
  BOOST_COST,
  PIN_COST,
} from "../../hooks/clubtender/useTransferPointsCost";
import { useSelector } from "react-redux";
import { ROLES } from "../../config/roles";
import {
  formatSalary as formatSalaryShared,
  getSalaryKind,
  formatDivisionLine as formatDivisionLineShared,
  isDraftRequest,
  isArchivedRequest,
} from "../../utils/tenderFormatters";

const StaffTenderCard = ({
  tender,
  onSaveStatusChange,
  type,
  isFromClub = false,
  staffInClub = true,
  isNewest = false,
  // Request drafts — same two optional props as club/TenderCard: the page's
  // transfer-window payload for the picker hint, and the list refetch.
  transferWindow = null,
  onDraftChanged = null,
}) => {
  const navigate = useNavigate();
  const { currentRole } = useSelector((state) => state.auth);

  // A coach/staff draft is still the club's own unpublished row: only the
  // owner's "My Requests" view can see one.
  const isDraft = type === "my-tenders" && isFromClub && isDraftRequest(tender);
  // Same gate for an archived (ended) row: Archive tab of the owner's view.
  const isArchived =
    type === "my-tenders" && isFromClub && isArchivedRequest(tender);
  // The handoff dims an archived card's informational middle.
  const archivedDim = isArchived ? "opacity-60" : "";
  const {
    draftLoading,
    publishDraftNow,
    scheduleDraft,
    removeDraftSchedule,
    deleteDraft,
    closeRequest,
    reactivateRequest,
  } = useClubTenderActions(tender, { onChanged: onDraftChanged });

  // Permission checks
  const canViewPitches = usePermission('view_pitches');
  const canDeleteTenders = usePermission('delete_tenders');
  const canCreateTenders = usePermission('create_tenders');
  const canContactClub = usePermission('contact_club');
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showBoostConfirmModal, setShowBoostConfirmModal] = useState(false);
  const [showPinConfirmModal, setShowPinConfirmModal] = useState(false);
  const { loadBalance, balanceLine, cannotAfford } = useTransferPointsCost();
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const menuRef = useRef(null);

  // Handle outside click to close the kebab menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const handlePitchClick = () => {
    if (type === "my-tenders" || type === "all-tenders") {
      // Check if current role is Coach, use coach routes
      if (currentRole === ROLES.Coach) {
        navigate(`/coach/tenders/${tender.id}/pitches`, {
          state: { tender: tender },
        });
      } else {
        // From ClubTenders - navigate to view pitches
        navigate(`/club/tenders/${tender.id}/pitches`, {
          state: { tender: tender },
        });
      }
    } else if (currentRole === ROLES.Coach || currentRole === ROLES.Staff) {
      // Saved tab, where "Pitch staff" is the card's only CTA. Coach and staff
      // have their own pitch routes; the agent route below is role-guarded and
      // bounces them to the home page. handleApplyPitch already holds that
      // coach/staff split, so reuse it instead of keeping a second copy in sync.
      handleApplyPitch();
    } else {
      // From AgentTenders - navigate to pitch page
      navigate(`/agent/tenders/${tender.id}/pitch`, {
        state: { tender: tender },
      });
    }
  };

  const handleSaveClick = async () => {
    if (loading) return;
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setLoading(true);
    try {
      let response;
      if (tender.is_saved) {
        response = await tenderService.unsaveTender(tender.id);
      } else {
        response = await tenderService.saveTender(tender.id);
      }

      if (response.success) {
        onSaveStatusChange(tender.id, !tender.is_saved);
      }
    } catch (error) {
      console.error("Error saving/unsaving tender:", error);
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  const handleMenuClick = () => {
    setShowMenu(!showMenu);
  };

  const handleBoostClick = () => {
    setShowMenu(false);
    setShowBoostConfirmModal(true);
    loadBalance();
  };

  const handlePinClick = () => {
    setShowMenu(false);
    setShowPinConfirmModal(true);
    // Unpinning is free, so it needs no balance and no gate.
    if (!tender.is_pinned) loadBalance();
  };

  const handleCloseClick = () => {
    setShowMenu(false);
    setShowCloseModal(true);
  };

  const handleReactivateClick = () => {
    setShowMenu(false);
    setShowReactivateModal(true);
  };

  // Archive instead of delete — same soft close as the player card, shared
  // hook, page refetch first and the old full reload only as fallback.
  const handleCloseConfirm = async () => {
    const ok = await closeRequest();
    if (ok !== false) {
      setShowCloseModal(false);
      if (!onDraftChanged) window.location.reload();
    }
  };

  const handleReactivateConfirm = async () => {
    const ok = await reactivateRequest();
    if (ok !== false) {
      setShowReactivateModal(false);
      if (!onDraftChanged) window.location.reload();
    }
  };

  const handleBoostConfirm = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const response = await boostTender(tender.id);
      if (response.success) {
        toast.success("Request boosted successfully!");
        setShowBoostConfirmModal(false);
        // Update tender status locally
        tender.is_boosted = true;
      } else {
        toast.error(response.error || "Failed to boost request");
        // A rejection used to leave the dialog standing with the same text it
        // had before. Re-reading the balance turns it into the answer: the
        // line under the headline states the real figure and the confirm
        // button goes dead if the points are not there.
        loadBalance();
      }
    } catch (error) {
      console.error("Error boosting tender:", error);
      toast.error("Failed to boost request. Please try again.");
      loadBalance();
    } finally {
      setLoading(false);
    }
  };

  const handlePinConfirm = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const response = tender.is_pinned
        ? await unpinTender(tender.id)
        : await pinTender(tender.id);

      if (response.success) {
        const action = tender.is_pinned ? "unpinned" : "pinned";
        toast.success(`Request ${action} successfully!`);
        setShowPinConfirmModal(false);
        // Update tender status locally
        tender.is_pinned = !tender.is_pinned;
        if (!tender.is_pinned && response.data?.pinned_tender_id) {
          tender.pinned_tender_id = response.data.pinned_tender_id;
        }
      } else {
        const action = tender.is_pinned ? "unpin" : "pin";
        toast.error(response.error || `Failed to ${action} request`);
        if (!tender.is_pinned) loadBalance();
      }
    } catch (error) {
      console.error("Error with pin/unpin tender:", error);
      const action = tender.is_pinned ? "unpin" : "pin";
      toast.error(`Failed to ${action} request. Please try again.`);
      if (!tender.is_pinned) loadBalance();
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPitch = () => {
    // Check if current role is Coach, use coach routes
    if (currentRole === ROLES.Coach) {
      navigate(`/coach/tenders/${tender.id}/pitch`, {
        state: { tender: tender },
      });
    } else {
      navigate(`/staff/tenders/${tender.id}/pitch`, {
        state: { tender: tender },
      });
    }
  };

  const handleMessageClub = async () => {
    if (loading) return;
    setLoading(true);
    try {
      console.log(tender);
      const clubId =
        tender?.club_id || tender?.club?.id || tender?.club?.club_id;
      if (!clubId) {
        toast.error("Club information not available for messaging");
        setLoading(false);
        return;
      }

      const response = await liveMarketService.createClubChat(clubId);
      if (response.success) {
        navigate("/inbox", {
          state: {
            channelId: response.channelId,
            channelType: response.channelType,
          },
        });
      } else {
        toast.error(response.error || "Failed to start chat with club");
      }
    } catch (error) {
      console.error("Error creating club chat:", error);
      toast.error("Failed to start chat with club");
    } finally {
      setLoading(false);
    }
  };

  // Format position string
  const formatPosition = (position) => {
    if (!position) return "";
    return position
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Salary + kind come from the shared, null-safe formatter so the card and
  // the loupe preview never diverge (the old local logic dropped any bound
  // that was 0, e.g. net_salary_from=0 / net_salary_to=1000 rendered blank).
  const getSalary = () => formatSalaryShared(tender) || "";
  const getSalaryType = () => getSalaryKind(tender) || "Net";

  // Format languages
  const getLanguages = () => {
    if (tender.language_skills && tender.language_skills.length > 0) {
      return tender.language_skills.join(", ");
    }
    return "-";
  };

  const handleClubClick = () => {
    const clubId = tender?.club?.club_id || tender?.club_id;
    if (clubId) {
      navigate(`/search/club/${clubId}`);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 border border-[#D2D4D880]">
      <div className="flex justify-between items-start mb-4">
        <div className="flex flex-col gap-2">
          {/* flex-wrap + min-w-0: a "Scheduled · 15 Aug, 09:00" pill is wider
              than BOOSTED / LITE / NEWEST and must wrap, not overflow. */}
          <div className="flex flex-wrap items-center gap-2 mb-1 min-w-0">
            <h3 className="text-lg font-semibold min-w-0 break-words">
              {formatPosition(tender.position)}
            </h3>
            {isDraft && <RequestDraftBadge tender={tender} />}
            {isArchived && <RequestArchivedBadge tender={tender} />}
            {tender.is_boosted && (
              <span className="px-2 py-0.5 bg-yellow-50 text-yellow-600 text-xs rounded-md">
                BOOSTED
              </span>
            )}
            {tender.is_lite && (
              <span className="px-2 py-0.5 bg-yellow-50 text-yellow-600 text-xs rounded-md">
                LITE
              </span>
            )}
            {isNewest && (
              <span className="px-2 py-0.5 bg-[#2283FF] text-white text-[10px] font-semibold rounded-full whitespace-nowrap">
                NEWEST
              </span>
            )}
          </div>
          {/* min-w-0: same reason as the player card — a flex child will not
              shrink below its own content width unless told to, so a long club
              name pushed this row out of the card. */}
          <div
            className="flex items-center gap-2 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleClubClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClubClick();
              }
            }}
            tabIndex={0}
            role="button"
            aria-label={`View ${tender.club.name} profile`}
          >
            <img
              src={tender.club.logo}
              alt={tender.club.name}
              className="w-8 h-8 object-contain"
              referrerPolicy="no-referrer"
            />
            <span className="text-[18px] text-[#242426] font-medium min-w-0 break-words">
              {tender.club.name}
            </span>
          </div>
          {/* DECISION 009 — a request an AGENCY posted for this club must be
              distinguishable from one the club wrote itself. Sits OUTSIDE the
              club-info block above, which is a "view club profile" button — the
              attribution is text, not part of that control. Renders only when
              the backend sends agent_attribution (agency-posted rows only). */}
          <PostedByAgentLine
            attribution={tender.agent_attribution}
            className="mt-1"
          />
        </div>
        <div className="flex items-center gap-2">
          {/* Attached-document paperclip (when present) — top-right cluster,
              separate from the top-left NEWEST badge (no collision). */}
          <AttachedDocLink tender={tender} size={5} />
          {/* Loupe: read-only quick preview, present for every role/type */}
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            aria-label="Preview request"
            title="Quick preview"
          >
            <Search className="w-5 h-5 text-[#535370]" />
          </button>

          {/* Only show save button if not from club */}
          {!isFromClub && (
            <button
              onClick={handleSaveClick}
              disabled={loading}
              className={`p-2 hover:bg-gray-100 rounded-lg ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <svg
                className={`w-5 h-5 ${
                  tender.is_saved
                    ? "text-[#2283FF] fill-current"
                    : "text-[#1D1B20]"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                />
              </svg>
            </button>
          )}
          
          {/* Show horizontal menu if from club */}
          {isFromClub && (
            <div className="relative" ref={menuRef}>
              <MoreHorizontal
                onClick={handleMenuClick}
                className="w-4 h-4 cursor-pointer"
              />
              {showMenu && (
                <div className="absolute right-0 top-6 bg-white py-4 px-2 shadow-lg border rounded-lg z-10 min-w-[160px]">
                  {/* Boost and Pin buy visibility in a feed neither a draft
                      nor an archived row is in; an archived row's menu is
                      Reactivate alone. Delete for a draft lives in its action
                      bar (which refetches instead of reloading). Edit Request
                      stays for live rows. */}
                  {/* Only show boost option if not already boosted */}
                  {!isDraft && !isArchived && !tender.is_boosted && (
                    <div
                      onClick={handleBoostClick}
                      className={`px-2 py-2 text-[#0A0F2D] text-sm cursor-pointer hover:bg-gray-50 ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {loading ? 'Processing...' : 'Boost this Request'}
                    </div>
                  )}
                  {/* Only show pin/unpin option */}
                  {isDraft || isArchived ? null : !tender.is_pinned ? (
                    <div
                      onClick={handlePinClick}
                      className={`px-2 py-2 text-[#0A0F2D] text-sm cursor-pointer hover:bg-gray-50 ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {loading ? 'Processing...' : 'Pin to top'}
                    </div>
                  ) : (
                    <div
                      onClick={handlePinClick}
                      className={`px-2 py-2 text-[#0A0F2D] text-sm cursor-pointer hover:bg-gray-50 ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {loading ? 'Processing...' : 'Unpin from top'}
                    </div>
                  )}
                  {!isArchived && canCreateTenders && (
                    <div
                      onClick={() => navigate(`/club/tenders/${tender.id}/edit`)}
                      className="px-2 py-2 text-[#0A0F2D] text-sm cursor-pointer hover:bg-gray-50"
                    >
                      Edit Request
                    </div>
                  )}
                  {/* Ending a live request is Close, not Delete — it moves
                      the row to the archive. Same permission gate the old
                      delete entry had. */}
                  {!isDraft && !isArchived && canDeleteTenders && (
                    <div
                      onClick={handleCloseClick}
                      className="px-2 py-2 text-[#0A0F2D] text-sm cursor-pointer hover:bg-gray-50"
                    >
                      Close request
                    </div>
                  )}
                  {isArchived && (
                    <div
                      onClick={handleReactivateClick}
                      className="px-2 py-2 text-[#0A0F2D] text-sm cursor-pointer hover:bg-gray-50"
                    >
                      Reactivate
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {formatDivisionLineShared(tender) && (
        <div className="flex items-center gap-2 mb-4">
          <p className="text-[#242426] text-sm font-medium">
            {formatDivisionLineShared(tender)}
          </p>
          <CountryFlag
            country={tender.division_country?.name}
            imageUrl={tender.division_country?.image_url}
            width={20}
            height={14}
          />
        </div>
      )}

      {/* Clamp stays; break-words makes it honest for unbreakable tokens. */}
      <p className={`text-sm text-[#535370] mb-4 overflow-hidden text-ellipsis line-clamp-2 break-words ${archivedDim}`}>
        {tender.description || "No description available"}
      </p>

      <div className={`flex justify-between items-center mb-4 bg-[#F7F7F7] p-2 rounded-lg ${archivedDim}`}>
        <div className="w-full flex justify-between items-center">
          {/* The platform captures this number per MONTH — the request form
              asks for "Minimum/Maximum Monthly {Net|Gross} Salary"
              (TenderFormDetails.js:542,578). Calling it yearly here was a
              factor of twelve against the same value shown as
              "{Net|Gross} Monthly salary" by TenderCard.js:705 and by the
              preview modal of the very same request. */}
          <p className="text-sm font-medium text-[#1A212F]">{getSalaryType()} Monthly salary</p>
          <p className="font-medium text-[#000000]">{getSalary()}</p>
        </div>
      </div>

      <div className={`grid grid-cols-1 gap-3 mb-6 ${archivedDim}`}>
        <div className="flex justify-between items-center">
          <p className="text-[12px] text-[#1A212F]">Request Type</p>
          <p
            className={`px-3 py-1 rounded-full text-sm ${
              tender.type_of_tender?.toLowerCase() === "full_time"
                ? "text-white bg-[#049669]"
                : tender.type_of_tender?.toLowerCase() === "contract"
                ? "bg-blue-100 text-blue-600"
                : "bg-orange-100 text-orange-600"
            }`}
          >
            {tender.type_of_tender?.charAt(0).toUpperCase() +
              tender.type_of_tender?.slice(1) || "N/A"}
          </p>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-[12px] text-[#1A212F]">Experience</p>
          <p className="text-[#242426]">{tender?.experience} years</p>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-[12px] text-[#1A212F]">Languages known</p>
          <p className="text-[#242426]">{getLanguages()}</p>
        </div>
      </div>

      {isDraft ? (
        /* A draft has no pitches to view. Same five actions as the player
           card, same component, same handlers. */
        <RequestDraftActions
          tender={tender}
          transferWindow={transferWindow}
          isSaving={draftLoading}
          onPreview={() => setShowPreview(true)}
          onPostNow={publishDraftNow}
          onSchedule={scheduleDraft}
          onRemoveSchedule={removeDraftSchedule}
          onDelete={deleteDraft}
        />
      ) : isArchived ? (
        /* An archived request's history and its way back replace "View
           pitches" — same component, same handlers as the player card. */
        <RequestArchiveActions
          tender={tender}
          isSaving={draftLoading}
          onReactivate={async () => {
            const ok = await reactivateRequest();
            if (ok !== false && !onDraftChanged) window.location.reload();
            return ok;
          }}
        />
      ) : !staffInClub ? (
        // Staff not in club - show Apply Pitch button
        <button
          onClick={handleApplyPitch}
          className="w-full py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 self-end"
        >
          Apply Pitch
        </button>
      ) : (type === "my-tenders" || type === "all-tenders") ? (
        // Staff in club - show View pitches or Message Club based on permissions
        <>
          {canViewPitches ? (
            <button
              onClick={handlePitchClick}
              className="w-full py-2 bg-white border border-[#E6E6E6] text-[#1E1E1E] rounded-lg hover:bg-blue-50 self-end"
            >
              View pitches ({tender.total_pitches})
            </button>
          ) : (
            <button
              onClick={handleMessageClub}
              disabled={loading || !canContactClub}
              className={`w-full py-2 bg-white border border-[#E6E6E6] text-[#1E1E1E] rounded-lg self-end ${
                loading || !canContactClub ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-50"
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full"></div>
                  <span>Connecting...</span>
                </div>
              ) : (
                <span>Message Club</span>
              )}
            </button>
          )}
        </>
      ) : (
        <button
          onClick={handlePitchClick}
          className="w-full py-2 bg-white border border-[#E6E6E6] text-[#1E1E1E] rounded-lg hover:bg-blue-50 self-end"
        >
          Pitch staff
        </button>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[400px]">
            <h3 className="text-lg font-semibold mb-2">
              {tender.is_saved ? "Unsave Request" : "Save Request"}
            </h3>
            <p className="text-gray-600 mb-6">
              {tender.is_saved
                ? "Are you sure you want to remove this request from your saved list?"
                : "Are you sure you want to save this request?"}
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSave}
                className="px-4 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Boost Confirmation Modal */}
      <ConfirmationModal
        isOpen={showBoostConfirmModal}
        onClose={() => setShowBoostConfirmModal(false)}
        onConfirm={handleBoostConfirm}
        variant="v2"
        title="Boost Request"
        message={`Are you sure you want to boost this ${formatPosition(tender.position)} Request? This will cost ${BOOST_COST} Transfer Points.`}
        /* The dialog named the price but never the balance, so the club found
           out it could not pay only AFTER confirming. `lines` and
           `confirmDisabled` already exist on this dialog — they were simply
           never used here. */
        lines={[balanceLine(BOOST_COST)]}
        confirmDisabled={cannotAfford(BOOST_COST)}
        confirmText="Boost Request"
        confirmButtonClass="bg-[#2283FF] hover:bg-blue-600"
        loading={loading}
      />

      {/* Pin Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPinConfirmModal}
        onClose={() => setShowPinConfirmModal(false)}
        onConfirm={handlePinConfirm}
        variant="v2"
        title={tender.is_pinned ? "Unpin Request" : "Pin Request"}
        message={
          tender.is_pinned
            ? `Are you sure you want to unpin this ${formatPosition(tender.position)} Request?`
            : `Are you sure you want to pin this ${formatPosition(tender.position)} Request to the top? This will cost ${PIN_COST} Transfer Points.`
        }
        /* Unpinning costs nothing, so it gets neither the balance line nor the
           gate — only pinning does. */
        lines={tender.is_pinned ? [] : [balanceLine(PIN_COST)]}
        confirmDisabled={!tender.is_pinned && cannotAfford(PIN_COST)}
        confirmText={tender.is_pinned ? "Unpin Request" : "Pin Request"}
        confirmButtonClass="bg-[#2283FF] hover:bg-blue-600"
        loading={loading}
      />

      {/* Close confirm — a plain confirm, no questions asked: the request
          wanders to the archive and stays reactivatable. */}
      <RequestConfirmDialog
        isOpen={showCloseModal}
        title="Close request"
        body="The request moves to your archive, is kept for 12 months and can be reactivated at any time."
        confirmLabel="Close request"
        loading={draftLoading}
        onClose={() => setShowCloseModal(false)}
        onConfirm={handleCloseConfirm}
      />

      {/* Reactivate confirm for the kebab entry (the action-bar button brings
          its own inside RequestArchiveActions). */}
      <RequestConfirmDialog
        isOpen={showReactivateModal}
        title="Reactivate this request?"
        body="It goes live again, agents are notified, and fresh timers start."
        confirmLabel="Reactivate"
        loading={draftLoading}
        onClose={() => setShowReactivateModal(false)}
        onConfirm={handleReactivateConfirm}
      />

      {/* Read-only quick-preview modal (loupe). Full untruncated tender +
          attached document, mirroring the card's primary action. */}
      <RequestPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        tender={tender}
        primaryAction={
          isDraft ? (
            <button
              onClick={async () => {
                const ok = await publishDraftNow();
                if (ok !== false) setShowPreview(false);
              }}
              disabled={draftLoading}
              className={`w-full min-h-[44px] rounded-lg bg-[#2283FF] font-semibold text-white hover:bg-blue-600 ${
                draftLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {draftLoading ? "Working..." : "Post now"}
            </button>
          ) : !staffInClub ? (
            <button
              onClick={() => {
                setShowPreview(false);
                handleApplyPitch();
              }}
              className="w-full py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600"
            >
              Apply Pitch
            </button>
          ) : type === "my-tenders" || type === "all-tenders" ? (
            canViewPitches ? (
              <button
                onClick={() => {
                  setShowPreview(false);
                  handlePitchClick();
                }}
                className="w-full py-2 bg-white border border-[#E6E6E6] text-[#1E1E1E] rounded-lg hover:bg-blue-50"
              >
                View pitches ({tender.total_pitches})
              </button>
            ) : (
              <button
                onClick={() => {
                  if (!canContactClub) return;
                  setShowPreview(false);
                  handleMessageClub();
                }}
                disabled={loading || !canContactClub}
                className={`w-full py-2 bg-white border border-[#E6E6E6] text-[#1E1E1E] rounded-lg ${
                  loading || !canContactClub
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-50"
                }`}
              >
                Message Club
              </button>
            )
          ) : (
            <button
              onClick={() => {
                setShowPreview(false);
                handlePitchClick();
              }}
              className="w-full py-2 bg-white border border-[#E6E6E6] text-[#1E1E1E] rounded-lg hover:bg-blue-50"
            >
              Pitch staff
            </button>
          )
        }
      />
    </div>
  );
};

export default StaffTenderCard;